// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  Grid,
  Column,
  Tile,
  Button,
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Tag,
  Search,
  Loading,
  InlineNotification,
} from '@carbon/react';
import { Play, BookmarkFilled, Time, User } from '@carbon/react/icons';

interface Course {
  id: string;
  title: string;
  description: string;
  instructor: string;
  duration: string;
  level: 'Beginner' | 'Intermediate' | 'Advanced';
  category: string;
  enrollmentCount: number;
  rating: number;
  thumbnail: string;
}

const PublicCoursesPage: React.FC = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Sample course data - in production this would come from API
  const sampleCourses: Course[] = [
    {
      id: '1',
      title: 'Introduction to AI in Education',
      description: "Learn the fundamentals of artificial intelligence and how it's transforming modern education.",
      instructor: 'Dr. Sarah Johnson',
      duration: '4 weeks',
      level: 'Beginner',
      category: 'AI & Technology',
      enrollmentCount: 1247,
      rating: 4.8,
      thumbnail: '/images/courses/ai-education.jpg',
    },
    {
      id: '2',
      title: 'Personalized Learning Strategies',
      description: 'Discover effective strategies for implementing personalized learning in your classroom or home.',
      instructor: 'Michael Chen',
      duration: '6 weeks',
      level: 'Intermediate',
      category: 'Teaching Methods',
      enrollmentCount: 893,
      rating: 4.6,
      thumbnail: '/images/courses/personalized-learning.jpg',
    },
    {
      id: '3',
      title: 'Family Engagement in Digital Learning',
      description: "Tools and techniques for parents to support their children's digital learning journey.",
      instructor: 'Emily Rodriguez',
      duration: '3 weeks',
      level: 'Beginner',
      category: 'Family Education',
      enrollmentCount: 567,
      rating: 4.9,
      thumbnail: '/images/courses/family-engagement.jpg',
    },
    {
      id: '4',
      title: 'Advanced Analytics for Educators',
      description: 'Deep dive into learning analytics and data-driven decision making in education.',
      instructor: 'Prof. David Kim',
      duration: '8 weeks',
      level: 'Advanced',
      category: 'Analytics',
      enrollmentCount: 334,
      rating: 4.7,
      thumbnail: '/images/courses/learning-analytics.jpg',
    },
  ];

  useEffect(() => {
    // Simulate API call
    const loadCourses = async () => {
      try {
        setLoading(true);
        // Simulate network delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        setCourses(sampleCourses);
      } catch (err) {
        setError('Failed to load courses. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadCourses();
  }, []);

  const filteredCourses = courses.filter(
    course =>
      course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'Beginner':
        return 'green';
      case 'Intermediate':
        return 'cyan';
      case 'Advanced':
        return 'red';
      default:
        return 'gray';
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loading description="Loading courses..." />
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
      <Grid>
        <Column lg={16}>
          <div style={{ marginBottom: '2rem' }}>
            <h1 style={{ fontSize: '2.5rem', fontWeight: 600, marginBottom: '1rem', color: '#0f62fe' }}>
              Public Course Catalog
            </h1>
            <p style={{ fontSize: '1.125rem', color: '#525252', maxWidth: '600px' }}>
              Explore our comprehensive collection of courses designed to enhance learning and teaching in the digital
              age.
            </p>
          </div>

          {error && (
            <InlineNotification
              kind="error"
              title="Error loading courses"
              subtitle={error}
              style={{ marginBottom: '2rem' }}
            />
          )}

          <div style={{ marginBottom: '2rem' }}>
            <Search
              placeholder="Search courses..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              size="lg"
            />
          </div>

          <Grid>
            {filteredCourses.map(course => (
              <Column key={course.id} lg={8} md={8} sm={4} style={{ marginBottom: '2rem' }}>
                <Tile style={{ height: '100%', padding: '1.5rem' }}>
                  <div style={{ marginBottom: '1rem' }}>
                    <Tag type={getLevelColor(course.level)} size="sm">
                      {course.level}
                    </Tag>
                    <Tag type="outline" size="sm" style={{ marginLeft: '0.5rem' }}>
                      {course.category}
                    </Tag>
                  </div>

                  <h3 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem' }}>{course.title}</h3>

                  <p style={{ color: '#525252', marginBottom: '1rem', lineHeight: 1.5 }}>{course.description}</p>

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '1rem',
                      fontSize: '0.875rem',
                      color: '#6f6f6f',
                    }}
                  >
                    <User size={16} style={{ marginRight: '0.5rem' }} />
                    <span style={{ marginRight: '1rem' }}>{course.instructor}</span>
                    <Time size={16} style={{ marginRight: '0.5rem' }} />
                    <span>{course.duration}</span>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '1rem',
                    }}
                  >
                    <span style={{ fontSize: '0.875rem', color: '#6f6f6f' }}>
                      {course.enrollmentCount.toLocaleString()} enrolled
                    </span>
                    <span style={{ fontSize: '0.875rem', color: '#6f6f6f' }}>⭐ {course.rating}/5.0</span>
                  </div>

                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <Button
                      kind="primary"
                      size="sm"
                      renderIcon={Play}
                      onClick={() => {
                        // In production, this would navigate to course details or require login
                        alert('Please log in to enroll in this course');
                      }}
                    >
                      Enroll Now
                    </Button>
                    <Button
                      kind="ghost"
                      size="sm"
                      renderIcon={BookmarkFilled}
                      onClick={() => {
                        alert('Course bookmarked! Log in to save your bookmarks.');
                      }}
                    >
                      Bookmark
                    </Button>
                  </div>
                </Tile>
              </Column>
            ))}
          </Grid>

          {filteredCourses.length === 0 && !loading && (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <h3>No courses found</h3>
              <p style={{ color: '#525252' }}>Try adjusting your search criteria or browse all available courses.</p>
            </div>
          )}
        </Column>
      </Grid>
    </div>
  );
};

export default PublicCoursesPage;
